import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, AlertCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import { fetchSeoMedicationIndex } from "../lib/api";
import SeoHead from "../components/SeoHead";
import ReadingProgressBar from "../components/ReadingProgressBar";
import { RelatedGuides } from "../components/RelatedGuides";

const SEVERITY_VISUAL = {
  HIGH: {
    chip: "bg-rose-100 text-rose-800 border-rose-200",
    Icon: AlertCircle,
    label: "HIGH",
  },
  MODERATE: {
    chip: "bg-amber-100 text-amber-900 border-amber-200",
    Icon: AlertTriangle,
    label: "MEDIUM",
  },
  LOW: {
    chip: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Icon: CheckCircle2,
    label: "LOW",
  },
};

const TYPE_LABEL = {
  PRESCRIPTION: "Prescription",
  OTC: "OTC",
  SUPPLEMENT: "Supplement",
};

function Skeleton() {
  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="medication-page-skeleton">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 space-y-6 animate-pulse">
        <div className="h-3 w-1/4 bg-cream-200 rounded" />
        <div className="h-12 w-3/4 bg-cream-200 rounded" />
        <div className="h-8 w-32 bg-cream-200 rounded-full" />
        <div className="h-32 w-full bg-cream-200 rounded-2xl" />
        <div className="h-40 w-full bg-cream-200 rounded-2xl" />
      </div>
    </main>
  );
}

function NotFound({ slug }) {
  return (
    <main className="bg-cream-50 min-h-[80vh] flex items-center justify-center px-5">
      <SeoHead
        title="Medication not found — Blendsafe"
        description="We couldn't find that medication in our catalog. Browse all medications."
        canonicalPath={`/medications/${slug}`}
      />
      <div
        data-testid="medication-page-not-found"
        className="max-w-md text-center space-y-5"
      >
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] text-ink">
          Medication not found.
        </h1>
        <p className="text-ink-soft">
          We couldn't find that medication in our catalog. Browse all medications
          or run a personalized check.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/browse"
            data-testid="medication-page-browse-cta"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Browse medications <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/check"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl border border-ink text-ink font-bold hover:bg-ink hover:text-cream-50 transition-all"
          >
            Run a check
          </Link>
        </div>
      </div>
    </main>
  );
}

function MedicationView({ data, slug }) {
  const med = data.medication || {};
  const interactions = data.interactions || [];
  const generic = med.generic_name || med.name || "";
  const brand = (med.brand_names && med.brand_names[0]) || generic;
  const label =
    brand && generic && brand !== generic ? `${generic} (${brand})` : (brand || generic);
  const typeLabel = TYPE_LABEL[med.type] || med.type || "Medication";
  const severity = (med.interaction_severity || "").toUpperCase();
  const severityVisual = SEVERITY_VISUAL[severity];

  const avoid = interactions.filter((i) =>
    ["HIGH", "MODERATE"].includes((i.risk_level || "").toUpperCase()),
  );
  const safe = interactions.filter(
    (i) => (i.risk_level || "").toUpperCase() === "LOW",
  );

  return (
    <main className="bg-cream-50 min-h-[80vh]" data-testid="medication-page">
      <ReadingProgressBar />
      <SeoHead
        title={data.meta?.title}
        description={data.meta?.description}
        canonicalPath={`/medications/${slug}`}
      />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14 space-y-8">
        <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-ink-mute">
          {typeLabel}
        </p>
        <div className="space-y-4">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-[-0.02em] text-ink leading-[1.05]">
            {label}: Skincare Safety Guide
          </h1>
          {severityVisual && (
            <span
              data-testid="medication-page-severity"
              className={`inline-flex items-center gap-2 px-4 h-9 rounded-full border font-bold text-sm ${severityVisual.chip}`}
            >
              <severityVisual.Icon className="w-4 h-4" /> Severity: {severityVisual.label}
            </span>
          )}
        </div>

        {med.description && (
          <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-3">
            <h2 className="text-lg font-bold text-ink">About {generic}</h2>
            <p className="text-ink-soft leading-relaxed text-[15px]">
              {med.description}
            </p>
          </section>
        )}

        <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-4">
          <h2 className="text-lg font-bold text-ink">What to avoid</h2>
          {avoid.length === 0 ? (
            <p className="text-ink-soft text-[14px]">
              No flagged ingredients in our database for {generic}.
            </p>
          ) : (
            <ul className="space-y-3" data-testid="medication-page-avoid">
              {avoid.map((row) => {
                const v = SEVERITY_VISUAL[row.risk_level] || SEVERITY_VISUAL.MODERATE;
                const Icon = v.Icon;
                return (
                  <li
                    key={row.ingredient_id}
                    className="flex items-start gap-3 pb-3 last:pb-0 border-b last:border-b-0 border-surface-line"
                  >
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 h-6 mt-0.5 rounded-full border font-bold text-[11px] uppercase tracking-wide shrink-0 ${v.chip}`}
                    >
                      <Icon className="w-3 h-3" /> {v.label}
                    </span>
                    <div className="text-[14px] leading-relaxed">
                      <Link
                        to={row.url || `/ingredients/${slug}/${row.ingredient_id}`}
                        data-testid={`medication-avoid-link-${row.ingredient_id}`}
                        className="font-bold text-ink underline decoration-from-font underline-offset-4 hover:text-ink-soft"
                      >
                        {row.ingredient_name}
                      </Link>
                      {row.summary && (
                        <span className="text-ink-soft"> — {row.summary}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-4">
          <h2 className="text-lg font-bold text-ink">No known conflict</h2>
          {safe.length === 0 ? (
            <p className="text-ink-soft text-[14px]">
              No explicitly low-risk pairings in our database. Run a personalized
              check below for a complete answer.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="medication-page-safe">
              {safe.map((row) => (
                <li key={row.ingredient_id} className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                  <div className="text-[14px] leading-relaxed">
                    <Link
                      to={row.url || `/ingredients/${slug}/${row.ingredient_id}`}
                      data-testid={`medication-safe-link-${row.ingredient_id}`}
                      className="font-bold text-ink underline decoration-from-font underline-offset-4 hover:text-ink-soft"
                    >
                      {row.ingredient_name}
                    </Link>
                    {row.summary && (
                      <span className="text-ink-soft"> — {row.summary}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {(med.usage_notes || med.notes) && (
          <section className="rounded-2xl bg-white border border-surface-line p-6 sm:p-7 space-y-3">
            <h2 className="text-lg font-bold text-ink">Usage notes</h2>
            <p className="text-ink-soft leading-relaxed text-[15px]">
              {med.usage_notes || med.notes}
            </p>
          </section>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Link
            to="/routine"
            data-testid="medication-page-cta"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Check your routine with {generic} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/check"
            className="text-sm font-semibold text-ink-soft underline decoration-from-font underline-offset-4 hover:text-ink"
          >
            or run a single check
          </Link>
        </div>

        <p className="text-[11px] text-ink-mute pt-4 border-t border-surface-line leading-relaxed">
          This information is for educational purposes only and is not a
          substitute for advice from your prescribing physician or
          dermatologist.
        </p>

        <RelatedGuides anchorType="medication" anchorId={med.id} />
      </div>
    </main>
  );
}

export default function MedicationPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    (async () => {
      try {
        const res = await fetchSeoMedicationIndex(slug);
        if (cancelled) return;
        setData(res);
        setStatus("ok");
      } catch (e) {
        if (cancelled) return;
        setStatus("notfound");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (status === "loading") return <Skeleton />;
  if (status === "notfound" || !data) return <NotFound slug={slug} />;
  return <MedicationView data={data} slug={slug} />;
}
